/* One modal shell; borrow the product's existing scene and controls, never clone a renderer. */
if (!customElements.get('product-applications')) {
  customElements.define('product-applications', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.tabs = this.querySelector('[data-application-tabs]');
      this.sceneSlot = this.querySelector('[data-application-stage]');
      this.tools = this.querySelector('[data-application-tools]');
      this.entries = [];
      // Cards use the same fullscreen editor, keeping the small image unobscured.
      document.addEventListener('click', event => {
        const button = event.target.closest('[data-edit]');
        if (!button) return;
        const entry = this.entries.find(item => item.renderer.editButton === button);
        const viewer = this.closest('product-tiling');
        if (!entry || typeof viewer?.open !== 'function') return;
        this.lastKey = entry.card.dataset.applicationKey;
        viewer.open('applications',button);
        if (viewer.dialog.open) {
          event.preventDefault(); event.stopImmediatePropagation();
        }
      }, {capture:true,signal});
      this.tabs.addEventListener('click', event => {
        const button = event.target.closest('[data-scene-index]');
        if (button) this.select(Number(button.dataset.sceneIndex));
      }, {signal});
      this.tabs.addEventListener('keydown', event => {
        if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key) || !this.entries.length) return;
        event.preventDefault();
        let index = this.entries.indexOf(this.active);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.entries.length - 1;
        else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.entries.length) % this.entries.length;
        this.select(index); this.tabs.children[index].focus();
      }, {signal});
      this.resizeObserver = new ResizeObserver(() => this.fit());
      this.resizeObserver.observe(this.sceneSlot);
      const refresh = () => this.refresh();
      document.addEventListener('shopify:section:load', refresh, {signal});
      document.addEventListener('shopify:section:unload', refresh, {signal});
      this.observer = new MutationObserver(records => {
        const isScene = node => node.nodeType === 1 && (node.matches('[data-application-product]') || node.querySelector('[data-application-product]'));
        if (records.some(record => [...record.addedNodes,...record.removedNodes].some(isScene))) this.refresh();
      });
      this.observer.observe(document.body, {childList:true,subtree:true});
      this.refresh();
      // These are renderer types, not a fixed set of scene names or categories.
      for (const type of ['cg-wall-mockup','cg-surface-mockup']) customElements.whenDefined(type).then(() => {
        if (this.isConnected) this.refresh();
      });
    }
    refresh() {
      const previous = new Map(this.entries.map(entry => [entry.card,entry]));
      this.entries = [...document.querySelectorAll('[data-application-product]')].filter(card =>
        card.dataset.applicationProduct === this.dataset.productId && !card.closest('[hidden]')
      ).map(card => {
        const renderer = card.querySelector('cg-wall-mockup,cg-surface-mockup') || previous.get(card)?.renderer;
        if (!renderer?.dataset.designSrc || typeof renderer.setEditing !== 'function') return null;
        const entry = previous.get(card) || {card,renderer};
        entry.label = card.dataset.applicationLabel || card.querySelector('figcaption')?.textContent.trim() || 'Application';
        return entry;
      }).filter(Boolean);
      this.tabs.replaceChildren(...this.entries.map((entry,index) => {
        const button = document.createElement('button');
        button.type = 'button'; button.role = 'tab'; button.textContent = entry.label;
        button.id = this.id + '-scene-' + index;
        button.dataset.sceneIndex = index;
        button.setAttribute('aria-controls',this.sceneSlot.id);
        return button;
      }));
      if (this.active && !this.entries.includes(this.active)) this.release();
      if (this.opened && !this.active && this.entries.length) this.select(0);
      // Keep scene definitions in the DOM for reuse, but remove the standalone section from layout.
      this.sourceSections ||= new Set();
      if (this.closest('product-tiling')?.source?.naturalWidth) {
        for (const entry of this.entries) {
          const section = entry.card.closest('.shopify-section, .applications-section');
          if (section && !section.contains(this)) {
            section.setAttribute('data-applications-source',''); this.sourceSections.add(section);
          }
        }
      }
      this.updateTabs();
      this.dispatchEvent(new CustomEvent('applications:availability', {bubbles:true,detail:{available:this.entries.length > 0}}));
    }
    open() {
      this.refresh();
      if (!this.entries.length) return false;
      this.opened = true;
      const header = this.closest('dialog')?.querySelector('.product-tiling__header');
      if (header) {
        header.insertBefore(this.tabs,header.querySelector('.product-tiling__close'));
        header.classList.add('product-applications__header');
      }
      this.select(Math.max(0,this.entries.findIndex(entry => entry.card.dataset.applicationKey === this.lastKey)));
      return true;
    }
    select(index) {
      const entry = this.entries[index];
      if (!entry || entry === this.active) return;
      this.release();
      this.active = entry;
      this.lastKey = entry.card.dataset.applicationKey;
      const {renderer} = entry;
      entry.placeholder = document.createComment('Application scene returns here');
      renderer.before(entry.placeholder);
      entry.controlPlaceholder = document.createComment('Application controls return here');
      renderer.controls.before(entry.controlPlaceholder);
      entry.wasEditing = renderer.editing;
      renderer.setAttribute('data-application-fullscreen','');
      this.sceneSlot.append(renderer);
      this.tools.append(renderer.controls);
      renderer.setEditing(true);
      this.fit();
      renderer.initialize();
      renderer.updateControls();
      this.updateTabs();
    }
    updateTabs() {
      [...this.tabs.children].forEach((button,index) => {
        const selected = this.entries[index] === this.active || (!this.active && index === 0);
        button.setAttribute('aria-selected',String(selected));
        button.tabIndex = selected ? 0 : -1;
        if (selected) this.sceneSlot.setAttribute('aria-labelledby',button.id);
      });
    }
    fit() {
      if (!this.active) return;
      const box = this.sceneSlot.getBoundingClientRect();
      // Show the whole scene at the largest size that fits; never crop its boundaries.
      const width = Math.max(0,Math.min(box.width,box.height * 1.5));
      const {renderer} = this.active;
      renderer.style.setProperty('--application-width',width + 'px');
      renderer.style.setProperty('--application-height',(width / 1.5) + 'px');
      renderer.schedule();
    }
    release() {
      const entry = this.active;
      if (!entry) return;
      this.active = null;
      const {renderer} = entry;
      entry.controlPlaceholder.replaceWith(renderer.controls);
      renderer.removeAttribute('data-application-fullscreen');
      renderer.style.removeProperty('--application-width');
      renderer.style.removeProperty('--application-height');
      renderer.setEditing(entry.wasEditing);
      if (entry.card.isConnected) entry.placeholder.replaceWith(renderer);
      else { renderer.remove(); entry.placeholder.remove(); }
      renderer.schedule();
    }
    close() {
      this.opened = false; this.release();
      this.tabs?.parentElement?.classList.remove('product-applications__header');
      if (this.tabs) this.prepend(this.tabs);
    }
    disconnectedCallback() {
      this.sourceSections?.forEach(section => section.removeAttribute('data-applications-source'));
      this.close(); this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); this.resizeObserver?.disconnect();
    }
  });
}
